import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { HeaderTemplate, Stepper, TitleHeader } from 'web-library';
import RouteKey from '../../../../routes/key';
import { fallbackEmptyString } from '../../../../utils/StringUtils';
import NonNationalExamTypeUuidDisplay from '../NonNationalExamTypeUuidDisplay';
import ConfirmAndPayList from '../../../../modules/examRegistration/components/nonNationalExam/ConfirmAndPayList';
import { usePostNonNationalExamRegistrationPayment } from '../../../../api-refactored/non-national-exam-registration/usePostNonNationalExamPayment';
import useStyles from '../style';
import { usePostGetConfirmAndPayData } from '../../../../api-refactored/non-national-exam-registration/usePostGetConfirmAndPayData';
import LoadingComponent from '../../../../components/LoadingComponent';
import { ParentParticularsLocation } from '../AEIS/ParentParticulars';
import VacancyExpiredModal from '../VacancyExpiredModal';
import { NonNationalExamNavigationContext } from '../../../../routes/ExamRoute';
import { useGetPassportDisabled } from '../../../../api-refactored/non-national-exam-registration/useGetPassportDisabled';
import { NonNexPassportDisableContext } from '../../../../routes/RootRouter';

interface ConfirmAndPayPageProps {
  routeSteps: { label: string | JSX.Element; disabled?: boolean }[];
}

const { exam } = RouteKey.child;
const { examRegistrations, receipt } = RouteKey.exam.child;

const SpersConfirmAndPay = ({ routeSteps }: ConfirmAndPayPageProps) => {
  const { state } = useLocation<ParentParticularsLocation>();
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const { uuid = '' } = useParams<{ uuid: string }>();
  const examTitle =
    t('exam->>RegisterExam.RegisterForAnExam') +
    (state?.isSupplementary ? ` - ${t('exam->>Instructions.Supplementary')}` : '');
  const [isDeclared, setIsDeclared] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<boolean>(false);
  const [isPaymentDisabled, setIsPaymentDisabled] = useState(false);
  const [isPassportDisabled, setIsPassportDisabled] = useState<boolean>(false);
  const nonNexPassportDisableContext = useContext(NonNexPassportDisableContext);

  const handleAgreement = () => {
    setErrMsg(false);
    setIsDeclared(!isDeclared);
  };

  /** to handle go back previous page/action
   * */
  const onBack = () => {
    history.goBack();
  };

  const { mutate, data: confirmAndPayData } = usePostGetConfirmAndPayData({
    onError: (err) => {
      if (err?.response?.data.code === '5012') {
        history.push(`/${exam}/${examRegistrations.route}`);
      }
    },
  });

  const retrieveData = useCallback(() => {
    mutate({
      uuid,
    });
  }, [mutate, uuid]);

  useEffect(() => {
    retrieveData();
  }, [retrieveData]);

  const candidateExamPayment = usePostNonNationalExamRegistrationPayment({});

  const handlePaymentOption = (
    callback: (internalTransactionRef: string, error?: string, errorMsg?: string) => void,
    method?: string
  ) => {
    candidateExamPayment.mutate(
      {
        uuid,
        paymentMode: fallbackEmptyString(method),
      },
      {
        onSuccess: (res) => {
          if (res?.paymentSuccess === true) {
            history.push(`${receipt.route}`);
          }
          callback(res.internalTransactionRef);
        },
        onError: (err) => {
          callback('', err?.response?.data.code, err?.response?.data.message);
        },
      }
    );
  };

  const handleClickPaymentButton = (callback: () => void) => {
    if (!isDeclared) {
      setErrMsg(true);
    } else {
      setErrMsg(false);
      callback();
    }
  };

  const handleNavigateOnSuccessPayment = () => {
    // TODO: Change routes push when payment success
    history.push(`${receipt.route}`, state);
  };

  useGetPassportDisabled({
    onSuccess: (res) => {
      setIsPassportDisabled(true);
      console.log(res);
      nonNexPassportDisableContext.setIsPassportDisabled(res);
    },
  });

  return (
    <>
      <TitleHeader title={examTitle} />
      <HeaderTemplate>
        <NonNationalExamTypeUuidDisplay nonExamType={state?.examTitle || '-'} enableApi={false} />
        <Stepper className={classes.stepper} activeStep={4} steps={routeSteps} />
      </HeaderTemplate>
      {confirmAndPayData ? (
        <ConfirmAndPayList
          data={confirmAndPayData}
          classes={classes}
          isAEIS={false}
          onPaymentButton={handleClickPaymentButton}
          onPaymentOption={handlePaymentOption}
          onSuccess={handleNavigateOnSuccessPayment}
          errMsg={errMsg}
          isDeclared={isDeclared}
          onAgreement={handleAgreement}
          onBack={onBack}
          isPaymentDisabled={isPaymentDisabled}
          onPaymentDisabled={(_isRetry) => setIsPaymentDisabled(_isRetry)}
          vacancyExpiredModal={<VacancyExpiredModal uuid={uuid} openModal />}
        />
      ) : (
        <LoadingComponent />
      )}
      <VacancyExpiredModal uuid={uuid} />
    </>
  );
};

export default SpersConfirmAndPay;
